import axios from 'axios';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import { toast } from 'react-toastify';
import { formatDate, getTranslation } from '.';
import { permissions } from '../data/permissions';
import userRoles from '../data/roles';
import { getProfileTypes } from '../service';

const uninterceptedAxiosInstance = axios.create();

export const getPermisssionFlagsForProfile = (profile) => {
  const createOrganization =
    profile.role === 'systemAdmin' ||
    (profile.permissions &&
      profile.permissions.some((permission) => permission._id === permissions.CREATE_ORGANIZATION));

  const manageSubSystemAdmins =
    profile.role === 'systemAdmin' ||
    (profile.permissions &&
      profile.permissions.some(
        (permission) => permission._id === permissions.MANAGE_SYSTEM_SUBADMINS
      ));

  const manageOrganizationSubAdmins =
    profile.role === 'systemAdmin' ||
    (profile.permissions &&
      profile.permissions.some(
        (permission) => permission._id === permissions.MANAGE_ORGANIZATION_SUBADMINS
      ));

  const manageOrganizationProfiles =
    profile.role === 'systemAdmin' ||
    (profile.permissions &&
      profile.permissions.some(
        (permission) => permission._id === permissions.MANAGE_ORGANIZATION_PROFILES
      ));

  return {
    createOrganization,
    manageSubSystemAdmins,
    manageOrganizationSubAdmins,
    manageOrganizationProfiles
  };
};

export const getErrorMessage = (error, message) => {
  if (typeof error?.response?.data === 'string') {
    return error.response.data;
  } else if (typeof error?.response?.data?.message === 'string') {
    return error.response.data.message;
  } else {
    return message;
  }
};

// Function to check if an element has a specific parent
export const hasSomeParentOrSelf = (el, selector) => {
  while (el) {
    if (el.matches(selector)) {
      return true;
    }
    el = el.parentElement;
  }
  return false;
};

export const hasPermission = (profile, targetRole, targetPermission) => {
  return (
    profile &&
    (profile.role === targetRole ||
      (Array.isArray(profile.permissions) &&
        profile.permissions.some((permission) => permission._id === targetPermission)))
  );
};

export const checkVideoUrlValidity = async (videoURL) => {
  try {
    const response = await uninterceptedAxiosInstance.head(`${videoURL}?_=${new Date().getTime()}`);
    // If the request completes successfully, the resource exists
    return response.status >= 200 && response.status < 300;
  } catch (error) {
    return false;
  }
};

export const getVideoUrl = (videoData) => {
  const isUpdated = videoData?.updatedAt ? Date.parse(videoData.updatedAt) : '';
  const currentTime = new Date();
  const parsedTime = Date.parse(currentTime);
  return `${process.env.REACT_APP_AWS_URL}profile-videos/${videoData?.videoURL}?date=${parsedTime}`;
};

// converted array to string
const getFieldValue = (fieldValue) => {
  return Array.isArray(fieldValue) ? fieldValue.join(', ') : fieldValue;
};

// New helper function to standardize header translation
const getTranslatedHeaders = (headers, t, translationPrefix) => {
  return headers
    .map((header) => {
      let name;
      switch (header.key) {
        case 'email':
          name = 'Email';
          break;
        case 'name':
          name = 'Name';
          break;
        case 'status':
          name = 'Status';
          break;
        case 'notes':
          name = 'Notes';
          break;
        case 'strength1':
          name = 'Strength 1';
          break;
        case 'strength2':
          name = 'Strength 2';
          break;
        case 'strength3':
          name = 'Strength 3';
          break;
        case 'role1':
          name = 'Role 1';
          break;
        case 'role2':
          name = 'Role 2';
          break;
        case 'role3':
          name = 'Role 3';
          break;
        default:
          name =
            getTranslation(t, `form.fields.${header.key}`, translationPrefix)?.label || header.name;
      }

      if (header.key.startsWith('educationInformation-')) {
        const parts = header.key.split('-');
        const index = parseInt(parts[1]);
        const field = parts[2];
        const baseName =
          getTranslation(t, `form.fields.educationInformation`, translationPrefix)?.label ||
          'Education Information';
        const fieldTranslation = getTranslation(
          t,
          `form.fields.educationInformation.${field}.export`,
          translationPrefix
        );
        name = `${baseName} - ${fieldTranslation} (${index + 1})`;
      }

      if (header.key.startsWith('companyInformation-')) {
        const parts = header.key.split('-');
        const index = parseInt(parts[1]);
        const field = parts[2];
        const baseName =
          getTranslation(t, `form.fields.companyInformation`, translationPrefix)?.label ||
          'Company Information';
        const fieldTranslation = getTranslation(
          t,
          `form.fields.companyInformation.${field}.export`,
          translationPrefix
        );
        name = `${baseName} - ${fieldTranslation} (${index + 1})`;
      }

      return { key: header.key, name };
    })
    .filter((header) => header.name);
};

// Helper function to sanitize and truncate sheet names
const sanitizeSheetName = (name, maxLength = 31) => {
  // Remove invalid characters that Excel doesn't allow in sheet names
  let sanitized = name.replace(/[\\/?*[\]]/g, '');
  // Truncate to maxLength while trying to keep words intact
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength - 3) + '...';
  }
  return sanitized;
};

export const processProfilesLocallyForExport = ({
  profiles,
  hasVideoFunction,
  videos = [],
  headers,
  groupName,
  fileName,
  t,
  translationPrefix
}) => {
  const hasVideoStrength = profiles.some((item) => 'videoStrength' in item);
  const hasVideoRole = profiles.some((item) => 'videoRole' in item);

  const videoHeaders = [];
  if (hasVideoFunction && videos.length > 0) {
    videoHeaders.push({ key: 'allVideosUploaded', name: 'Videos' });
  }

  profiles.forEach((profile) => {
    profile.status = profile?.settings?.isApproved
      ? 'Validated'
      : profile?.settings?.isNonStarted
      ? 'Not Started'
      : 'In Progress';

    profile.hasProfileImage = profile?.settings?.hasProfileImage ? 'True' : 'False';

    if (hasVideoFunction && videos.length > 0) {
      profile.allVideosUploaded =
        profile.settings?.videosCount >= profile.settings?.videosRequired ? 'True' : 'False';

      for (let i = 0; i < videos.length; i++) {
        const targetVideo = profile?.videos?.find(
          (video) => Number(video.videoID) === Number(videos[i]?.videoPage?.index)
        );
        profile[`video-upload-date-${videos[i].videoPage?.index}`] = targetVideo?.updatedAt
          ? formatDate(targetVideo?.updatedAt)
          : '';
      }
    }
  });

  const exportHeaders = [
    { key: 'email', name: 'Email' },
    { key: 'status', name: 'Status' },
    { key: 'hasProfileImage', name: 'Profile Image' },
    ...videoHeaders,
    ...headers,
    ...(hasVideoStrength ? [{ key: 'videoStrength', name: 'Video Strength' }] : []),
    ...(hasVideoRole ? [{ key: 'videoRole', name: 'Video Role' }] : []),
    { key: 'notes', name: 'Notes' }
  ];

  const educationFields = [
    'typeOfEstablishment',
    'nameOfEstablishment',
    'cityOfEstablishment',
    'departmentOfEstablishment',
    'diplomaTitle',
    'diplomaCompleted',
    'current'
  ];
  const companyFields = ['name', 'city', 'department', 'current'];

  const educationInformationHeaderIndex = exportHeaders.findIndex(
    (header) => header.key === 'educationInformation'
  );

  if (educationInformationHeaderIndex !== -1) {
    const numberOfEducationInformation = profiles.reduce((max, profile) => {
      return Math.max(max, profile.educationInformation?.length || 0);
    }, 0);
    const newHeaders = [];
    for (let i = 0; i < numberOfEducationInformation; i++) {
      educationFields.forEach((field) => {
        newHeaders.push({
          key: `educationInformation-${i}-${field}`,
          name: `educationInformation-${i}-${field}`
        });
      });
    }
    exportHeaders.splice(educationInformationHeaderIndex, 1, ...newHeaders);
  }

  const companyInformationHeaderIndex = exportHeaders.findIndex(
    (header) => header.key === 'companyInformation'
  );

  if (companyInformationHeaderIndex !== -1) {
    const numberOfCompanyInformation = profiles.reduce((max, profile) => {
      return Math.max(max, profile.companyInformation?.length || 0);
    }, 0);
    const newHeaders = [];
    for (let i = 0; i < numberOfCompanyInformation; i++) {
      companyFields.forEach((field) => {
        newHeaders.push({
          key: `companyInformation-${i}-${field}`,
          name: `companyInformation-${i}-${field}`
        });
      });
    }
    exportHeaders.splice(companyInformationHeaderIndex, 1, ...newHeaders);
  }

  if (hasVideoFunction && videos.length > 0) {
    for (let i = 0; i < videos.length; i++) {
      exportHeaders.push({
        key: `video-upload-date-${videos[i].videoPage?.index}`,
        name: `Video ${i + 1} Date (DD/MM/YYYY)`
      });
    }
  }

  exportData(exportHeaders, [{ name: groupName, profiles }], fileName, t, translationPrefix);
};

export const exportData = (
  headers,
  data,
  fileName = 'Profiles-Data',
  t,
  translationPrefix,
  sheetName = 'Profiles',
  sortEntry = 'worldSkillsProfession'
) => {
  try {
    const translatedHeaders = getTranslatedHeaders(headers, t, translationPrefix);
    const headerKeys = translatedHeaders.map((header) => header.key);
    const wb = XLSX.utils.book_new();

    // Process each data group
    data.forEach((group) => {
      // Initialize sheet with translated headers for each group
      const groupWorksheet = XLSX.utils.json_to_sheet([], {
        header: translatedHeaders.map((header) => header.name)
      });

      // Sort profiles if sortEntry exists in the data
      let sortedProfiles = group.profiles;
      if (sortEntry && group.profiles.length > 0 && group.profiles[0].hasOwnProperty(sortEntry)) {
        sortedProfiles = [...group.profiles].sort((a, b) => {
          const aValue = a[sortEntry];
          const bValue = b[sortEntry];

          // Check if the values have FR locale property
          const aFrValue = aValue && typeof aValue === 'object' && aValue.fr ? aValue.fr : aValue;
          const bFrValue = bValue && typeof bValue === 'object' && bValue.fr ? bValue.fr : bValue;

          // Perform locale-aware string comparison for FR
          if (typeof aFrValue === 'string' && typeof bFrValue === 'string') {
            return aFrValue.localeCompare(bFrValue, 'fr', {
              numeric: true,
              sensitivity: 'base'
            });
          }

          // Fallback to regular comparison
          return String(aFrValue).localeCompare(String(bFrValue), 'fr', {
            numeric: true,
            sensitivity: 'base'
          });
        });
      }

      // Process and add profiles to the sheet, ensuring data matches the headers
      sortedProfiles.forEach((dataItem) => {
        // Create a new object that includes all keys from headerKeys, using a default value for missing keys
        const filteredDataItem = headerKeys.reduce((acc, key) => {
          // Use the header key for the key, defaulting to a specific value if the key doesn't exist in the dataItem
          acc[key] = dataItem.hasOwnProperty(key) ? getFieldValue(dataItem[key]) : ''; // Default value can be 'N/A', null, or ''
          return acc;
        }, {});

        // Directly adding filtered data to the worksheet
        XLSX.utils.sheet_add_json(groupWorksheet, [filteredDataItem], {
          skipHeader: true,
          origin: -1
        });
      });

      // Use group name as sheet name, fallback to default if not available
      const rawSheetName = group.name || `${sheetName}-${group.id}`;
      const groupSheetName = sanitizeSheetName(rawSheetName);

      // Append the populated worksheet to the workbook with the provided sheet name
      XLSX.utils.book_append_sheet(wb, groupWorksheet, groupSheetName);
    });

    // Write the workbook to a file
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  } catch (error) {
    console.error('Failed to export data', error);
    throw new Error('Failed to export data');
  }
};

export const exportDataSingleSheet = (
  headers,
  data,
  fileName = 'Profiles-Data',
  t,
  translationPrefix,
  sheetName = 'Profiles',
  groupColumnName = 'Group',
  sortEntry = 'worldSkillsProfession'
) => {
  try {
    const translatedHeaders = getTranslatedHeaders(headers, t, translationPrefix);
    const headerKeys = translatedHeaders.map((header) => header.key);
    const wb = XLSX.utils.book_new();

    // Create headers array with group name as first column
    const finalHeaders = [groupColumnName, ...translatedHeaders.map((header) => header.name)];

    // Initialize single sheet with headers including group column
    const worksheet = XLSX.utils.json_to_sheet([], {
      header: finalHeaders
    });

    // Process each data group and combine all profiles into single sheet
    data.forEach((group) => {
      const groupName = group.name || `Group-${group.id}`;

      // Sort profiles if sortEntry exists in the data
      let sortedProfiles = group.profiles;
      if (sortEntry && group.profiles.length > 0 && group.profiles[0].hasOwnProperty(sortEntry)) {
        sortedProfiles = [...group.profiles].sort((a, b) => {
          const aValue = a[sortEntry];
          const bValue = b[sortEntry];

          // Check if the values have FR locale property
          const aFrValue = aValue && typeof aValue === 'object' && aValue.fr ? aValue.fr : aValue;
          const bFrValue = bValue && typeof bValue === 'object' && bValue.fr ? bValue.fr : bValue;

          // Perform locale-aware string comparison for FR
          if (typeof aFrValue === 'string' && typeof bFrValue === 'string') {
            return aFrValue.localeCompare(bFrValue, 'fr', {
              numeric: true,
              sensitivity: 'base'
            });
          }

          // Fallback to regular comparison
          return String(aFrValue).localeCompare(String(bFrValue), 'fr', {
            numeric: true,
            sensitivity: 'base'
          });
        });
      }

      // Process and add profiles to the sheet
      sortedProfiles.forEach((dataItem) => {
        // Create a new object that includes all keys from headerKeys, using a default value for missing keys
        const filteredDataItem = headerKeys.reduce((acc, key) => {
          acc[key] = dataItem.hasOwnProperty(key) ? getFieldValue(dataItem[key]) : '';
          return acc;
        }, {});

        // Add group name as the first column, followed by profile data
        const rowData = {
          [groupColumnName]: groupName,
          ...filteredDataItem
        };

        // Add the row data to the worksheet
        XLSX.utils.sheet_add_json(worksheet, [rowData], {
          skipHeader: true,
          origin: -1
        });
      });
    });

    // Append the populated worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, worksheet, sheetName);

    // Write the workbook to a file
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  } catch (error) {
    console.error('Failed to export data to single sheet', error);
    throw new Error('Failed to export data to single sheet');
  }
};

export const exportAllData = async (data, t, translationPrefix) => {
  try {
    const zip = new JSZip();

    // Process each main group
    data.forEach((group) => {
      const wb = XLSX.utils.book_new();
      const translatedHeaders = getTranslatedHeaders(group.headers, t, translationPrefix);
      const headerKeys = translatedHeaders.map((header) => header.key);

      // Process each organization in the group's data
      group.data.forEach((org) => {
        // Initialize sheet with translated headers
        const sheet = XLSX.utils.json_to_sheet([], {
          header: translatedHeaders.map((header) => header.name)
        });

        // Process profiles in each organization
        org.profiles.forEach((profile) => {
          const filteredDataItem = headerKeys.reduce((acc, key) => {
            acc[key] = profile.hasOwnProperty(key) ? getFieldValue(profile[key]) : '';
            return acc;
          }, {});

          // Add the profile data to the sheet
          XLSX.utils.sheet_add_json(sheet, [filteredDataItem], {
            skipHeader: true,
            origin: -1
          });
        });

        // Use sanitized organization name for the sheet name
        const orgSheetName = sanitizeSheetName(org.name || `Organization-${org.id}`);
        XLSX.utils.book_append_sheet(wb, sheet, orgSheetName);
      });

      // Convert workbook to array buffer
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

      // Add the Excel file to the zip
      const fileName = sanitizeSheetName(group.name || `Group-${group.id}`);
      zip.file(`${fileName}.xlsx`, excelBuffer);
    });

    // Generate the zip file and trigger download
    const zipContent = await zip.generateAsync({ type: 'blob' });
    saveAs(zipContent, 'Exported-Data.zip');
  } catch (error) {
    console.error('Failed to export data: ', error);
    toast.error('Failed to export data.');
  }
};

export function hexToRgba(hex, opacity) {
  if (!hex) return `rgba(12, 34, 56, 1)`;
  // Remove the # symbol from the hex code
  hex = hex.replace('#', '');

  // Convert the hex code to RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Convert the opacity percentage to a decimal value
  const alpha = parseInt(opacity) / 100;

  // Create the RGBA string
  const rgba = `rgba(${r}, ${g}, ${b}, ${alpha})`;

  return rgba;
}

export const getProfileUrl = async (profileType, profileId) => {
  try {
    const { data } = await getProfileTypes();
    const profileTypeData = data.find((type) => type._id === profileType);
    let setUrlStructure;

    if (profileTypeData?.profileUrlStructure) {
      try {
        setUrlStructure = profileTypeData?.profileUrlStructure?.replace('{{userId}}', profileId);
        setUrlStructure = new URL(setUrlStructure);

        setUrlStructure.searchParams.set('adminProfileView', 'true');
      } catch (error) {
        console.log('error with initial url', error);
        try {
          setUrlStructure = new URL(setUrlStructure, 'https://www.mission-future.com');
          setUrlStructure.searchParams.set('adminProfileView', 'true');
        } catch (error) {
          console.log('error with adding domain as we have to do it manually', error);
        }
      }

      if (setUrlStructure && setUrlStructure.href && setUrlStructure.href.includes(profileId)) {
        return setUrlStructure.href;
      }
    }
  } catch (error) {
    console.log('error with getting profile types', error);
  }

  if (userRoles.COMPETITOR.includes(profileType)) {
    return `https://www.mission-future.com/competiteur-profile/${profileId}?adminProfileView=true`;
  } else if (
    profileType === userRoles.CH_CHAMPION ||
    profileType === userRoles.CH_AMBASSADOR ||
    profileType === userRoles.CH_NATIONAL_CHAMPION
  ) {
    return `https://www.mission-future.com/ch/champion-profile/${profileId}?adminProfileView=true`;
  } else {
    return `https://www.mission-future.com/champion-profile/${profileId}?adminProfileView=true`;
  }
};
